<template>
  <div class="s-strip s-leaseitnow-car">
    <div class="page-container">
      <Loader v-if="isLoading"></Loader>
      <Alert
        v-if="errorMsgs.length"
        type="danger"
        :messages="errorMsgs"></Alert>

      <div ref="stickyParent" class="row gx-5">
        <div class="col-lg-8">
          <div class="s-leaseitnow-car__header mb-20">
            <h1 class="h1 extrabold mb-5">
              {{ carObj?.manufacturer }} {{ carObj?.short_model_name }}
            </h1>
            <h2 class="h4">{{ modelNameComputed }}</h2>
          </div>

          <div class="s-leaseitnow-car__gallery mb-32">
            <VehicleGallerySlick
              v-if="recordsGallery.length"
              :records="recordsGallery" />

            <div
              v-if="recordsGallery.length"
              class="d-flex justify-content-end">
              <small>
                <i>{{ dictionary.galleryDisclaimer }}</i>
              </small>
            </div>
          </div>

          <AccordionLarge
            :title="dictionary.techTitle"
            :is-active-prop="!isDevice"
            class="theme-grey theme-icon-cirle mb-40">
            <VehicleTechs
              v-if="carObj"
              :item="carObj"
              :hide-title="true"
              style="padding: 20px 0" />
          </AccordionLarge>

          <AccordionLarge
            :title="dictionary.benefitsTitle"
            :is-active-prop="!isDevice"
            class="theme-grey theme-icon-cirle mb-40">
            <VehicleBenefits
              :records="vehicleBenefits"
              :columns="2"
              :hide-title="true"
              class="mb-40"
              style="padding: 20px 0" />
          </AccordionLarge>

          <AccordionLarge
            :title="dictionary.standardAccordionHeaderTitle"
            :is-active-prop="false"
            class="theme-grey theme-icon-cirle mb-40">
            <Accordion
              v-for="(item, index) in specsGroups"
              :key="index"
              :title="getSpecGroupTitle(item)"
              :is-active-prop="index === 0">
              <ul class="is-checked">
                <li
                  v-for="entry in getSpecGroupEntries(item)"
                  :key="entry.spec_id">
                  {{ entry.spec_name }}
                </li>
              </ul>
            </Accordion>
          </AccordionLarge>

          <AccordionLarge
            v-if="allOptions.length"
            :title="dictionary.optionsAccordionHeaderTitle"
            :is-active-prop="!isDevice"
            class="theme-yellow theme-icon-cirle"
            :class="{ 'mb-40': isDevice }">
            <section
              v-if="dictionary.extrasDisclaimer && allOptions.length"
              class="s-contact-note--small-size mt-20">
              <div class="s-contact-note__body">
                <div
                  class="s-contact-note__details align-items-center"
                  style="flex-direction: row">
                  <div class="s-contact-note__picture">
                    <IdeaLottie width="20"></IdeaLottie>
                  </div>
                  <div>
                    <div
                      class="s-contact-note__description"
                      v-html="dictionary.extrasDisclaimer"></div>
                  </div>
                </div>
              </div>
            </section>

            <Accordion
              v-if="packagesOptions.length"
              :title="dictionary.optionsAccordionPackagesTitle"
              :is-active-prop="true">
              <ul class="b-extra-equipment-checkboxes">
                <li v-for="item in packagesOptions" :key="item.optionId">
                  <div class="form-check">
                    <input
                      :id="item.optionId"
                      v-model="selected"
                      :value="item.optionId"
                      type="checkbox"
                      class="form-check-input"
                      autocomplete="off"
                      @change="onChangeOption" />
                    <label class="form-check-label" :for="item.optionId">
                      <div>
                        <span v-if="item.optionCode">
                          {{ item.optionCode }}&nbsp;-&nbsp;
                        </span>
                        <span>{{ item.name }}</span>
                      </div>
                    </label>
                  </div>

                  <em class="h4 bold">€{{ calculatedPrice(item) }}</em>
                </li>
              </ul>
            </Accordion>

            <Accordion
              v-if="equipmentOptions.length"
              :title="dictionary.optionsAccordionEquipmentTitle"
              :is-active-prop="true">
              <!-- <div class="mb-20">selected: {{ selected }}</div> -->

              <ul class="b-extra-equipment-checkboxes">
                <li v-for="item in equipmentOptions" :key="item.optionId">
                  <div class="form-check">
                    <input
                      :id="item.optionId"
                      v-model="selected"
                      :value="item.optionId"
                      type="checkbox"
                      class="form-check-input"
                      autocomplete="off"
                      @change="onChangeOption" />
                    <label class="form-check-label" :for="item.optionId">
                      <div>
                        <span v-if="item.optionCode">
                          {{ item.optionCode }}&nbsp;-&nbsp;
                        </span>
                        <span>{{ item.name }}</span>
                      </div>
                    </label>
                  </div>

                  <em class="h4 bold">€{{ calculatedPrice(item) }}</em>
                </li>
              </ul>
            </Accordion>

            <Accordion
              v-if="colorsOptions.length"
              :title="dictionary.optionsAccordionColorTitle"
              :is-active-prop="true">
              <ul class="b-extra-equipment-checkboxes">
                <li v-for="item in colorsOptions" :key="item.optionId">
                  <div class="form-check">
                    <input
                      :id="item.optionId"
                      v-model="selected"
                      :value="item.optionId"
                      type="checkbox"
                      class="form-check-input"
                      autocomplete="off"
                      @change="onChangeOption" />
                    <label class="form-check-label" :for="item.optionId">
                      <div>
                        <span v-if="item.optionCode">
                          {{ item.optionCode }}&nbsp;-&nbsp;
                        </span>
                        <span>{{ item.name }}</span>
                      </div>
                    </label>
                  </div>

                  <em class="h4 bold">€{{ calculatedPrice(item) }}</em>
                </li>
              </ul>
            </Accordion>
          </AccordionLarge>
        </div>
        <div class="col-lg-4 sticky-sidebar">
          <div class="sticky-element">
            <VehicleRentalCalculator
              v-if="carObj"
              :car-obj="carObj"
              @change-config="onChangeConfig"
              @change-vat="onChangeVat"
              @click-request="onClickRequestOffer" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-if="carObj" class="s-leaseitnow-car-bar d-none d-lg-flex">
    <div class="page-container">
      <ul>
        <li>
          <span>{{ dictionary.retailPriceBeforeTaxesLabel }}</span>
          <span class="value">€{{ priceExclTaxFormatted }}</span>
        </li>
        <li>
          <span>{{ dictionary.retailPriceLabel }}</span>
          <span class="value">€{{ priceFormatted }}</span>
        </li>
        <li :class="{ 'is-invisibile': extraPrice === 0 }">
          <span
            data-bs-toggle="tooltip"
            :title="dictionary.extrasPriceTooltip"
            class="d-flex"
            style="gap: 5px">
            {{ dictionary.extrasPriceLabel }}
            <LazyImage
              :image="{
                url: '/imgs/icons/tooltip.svg',
                alt: 'Tooltip',
              }"
              width="16" />
          </span>

          <span class="value">€{{ extraPriceFormatted }}</span>
        </li>
        <li :class="{ 'is-invisibile': extraPrice === 0 }">
          <span>{{ dictionary.finalPriceLabel }}</span>
          <span class="value">€{{ finalPriceFormatted }}</span>
        </li>
        <li>
          <button
            type="button"
            class="btn btn-primary btn-height-short"
            @click="onClickRequestOffer">
            {{ dictionary.buttonRequestOfferText }}
          </button>
        </li>
      </ul>
    </div>
  </div>

  <ModalExtrasRequired
    :key="key"
    :master="lastSelected"
    :childs-included="extrasIncludedChilds"
    :childs-required="extrasRequiredChilds"
    :options="allOptions"
    :selected-options="selected"
    @undo="onUndoExtras"
    @submit="onSubmitExtras" />

  <ModalExtrasNeeded
    :master="lastSelected"
    :childs="extrasNeededChilds"
    :options="allOptions"
    @undo="onUndoExtrasNeeded"
    @submit="onSubmitExtrasNeeded" />

  <ModalExtrasExcluded :childs="extrasExcludedChilds" />
</template>

<script>
import IdeaLottie from '../components/lotties/Idea.vue'
import { Modal } from 'bootstrap'
import * as lib from './../../lib/lib'
import axios from 'axios'
import AccordionLarge from './../components/BookingAccordion.vue'
import Accordion from './../components/common/Accordion.vue'
import VehicleGallerySlick from './../components/leaseitnow/VehicleGallerySlick.vue'
import Alert from '../components/common/Alert.vue'
import Loader from '../components/common/Loader.vue'
import VehicleRentalCalculator from './../components/leaseitnow/VehicleRentalCalculator.vue'
import mixinsLeaseItNow from './../mixins/leaseitnow-mixins'
/* eslint-disable no-unused-vars */
import { difference, last, remove, sum, uniqBy } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import constants from './../../lib/constants'
import ModalExtrasRequired from './../components/leaseitnow/ModalExtrasRequired.vue'
import ModalExtrasExcluded from './../components/leaseitnow/ModalExtrasExcluded.vue'
import ModalExtrasNeeded from './../components/leaseitnow/ModalExtrasNeeded.vue'
import StickToParent from './../../modules/StickToParent'
import VehicleTechs from './../components/leaseitnow/VehicleTechs.vue'
import VehicleBenefits from './../components/VehicleBenefits.vue'
import LazyImage from './../components/common/LazyImage.vue'

export default {
  components: {
    Alert,
    Loader,
    VehicleGallerySlick,
    VehicleTechs,
    VehicleBenefits,
    AccordionLarge,
    Accordion,
    VehicleRentalCalculator,
    ModalExtrasRequired,
    ModalExtrasExcluded,
    ModalExtrasNeeded,
    LazyImage,
    IdeaLottie,
  },
  mixins: [mixinsLeaseItNow],

  beforeRouteLeave() {
    this.showHeaderRequestOfferButton()
  },

  data() {
    return {
      key: uuidv4(),
      lang: 'en',
      isLoading: false,
      errorMsgs: [],
      // data sources
      carObj: null,
      // options data sources
      allOptions: [],
      equipmentOptions: [],
      colorsOptions: [],
      packagesOptions: [],

      selected: [],

      vehicleBenefits: [],

      // case 1
      extrasIncludedChilds: [],
      // case 2
      extrasRequiredChilds: [],
      // case 3
      extrasExcludedChilds: [],
      // case 4
      extrasNeededChilds: [],
      // modals instances
      modalExtrasRequired: null,
      modalExtrasExcluded: null,
      modalExtrasNeeded: null,

      // helpers
      lastSelected: null,
      lastChecked: false,
      vatIncluded: false,
    }
  },
  computed: {
    isDevice() {
      return lib.scrDevices()
    },
    modelNameComputed() {
      if (!this.carObj) {
        return null
      }
      /* eslint-disable camelcase */
      const { model_name, short_model_name } = this.carObj
      const modelName = model_name.replace(short_model_name, '')

      return `${modelName} ${this.carObj.model_key}`
    },

    priceExclTaxFormatted() {
      return this.carObj
        ? Math.round(this.carObj.price_excl_tax_num).toLocaleString(this.lang)
        : null
    },

    priceFormatted() {
      return this.carObj
        ? this.carObj.price_num.toLocaleString(this.lang)
        : null
    },

    extraPriceFormatted() {
      return Math.round(this.extraPrice).toLocaleString(this.lang)
    },

    finalPriceFormatted() {
      return Math.round(this.finalPrice).toLocaleString(this.lang)
    },

    specsGroups() {
      if (!this.carObj) {
        return []
      }

      return Object.keys(this.carObj.sorted_specs)
    },

    recordsGallery() {
      if (!this.carObj) {
        return []
      }

      return this.carObj.slider_images.map((x) => ({
        id: uuidv4(),
        thumbnail: { url: x, alt: '' },
        image: { url: x, alt: '' },
      }))
    },
  },
  watch: {
    /* eslint-disable no-undef */
    selected(newValue, oldValue) {
      if (newValue.length > oldValue.length) {
        this.lastChecked = true
        this.lastSelected = newValue.length ? _.last(newValue) : null
      } else {
        this.lastChecked = false
        this.lastSelected = oldValue.length
          ? _.difference(oldValue, newValue).join()
          : null
      }
    },
  },
  mounted() {
    const { hasRentalArgs, hasExtrasArgs } = this.$route.query
    if (hasExtrasArgs !== 'true') {
      this.removeCarArgs('extras')
    }

    if (hasRentalArgs !== 'true') {
      this.removeCarArgs('rental')
    }

    this.hideHeaderRequestOfferButton()

    const { vehicleBenefits } = lib.getData('leaseItNowData')

    this.lang = lib.getLocaleFromLangAttr()

    this.vehicleBenefits = vehicleBenefits

    this.setPreselectedValues()
    this.setPreselectedExtras()
    this.getCar()
    this.getCarOptions()

    this.modalExtrasExcluded = new Modal(
      document.getElementById('modalExtrasExcluded')
    )

    this.modalExtrasNeeded = new Modal(
      document.getElementById('modalExtrasNeeded')
    )

    this.$nextTick(() => {
      this.stickToParent = new StickToParent(this.$refs.stickyParent)
    })
  },
  methods: {
    calculatedPrice(item) {
      if (!this.configuration.vat) {
        return item.price.toLocaleString(this.lang)
      }
      const vat = this.configuration.vat / 100 + 1
      return (item.price * vat).toLocaleString(this.lang)
    },

    showModalExtrasRequired() {
      const options = { keyboard: false, backdrop: 'static' }

      this.modalExtrasRequired = new Modal(
        document.getElementById('modalExtrasRequired'),
        options
      )

      this.modalExtrasRequired.show()
    },

    getSpecGroupTitle(value) {
      return this.carObj.sorted_specs[value][0]
    },

    getSpecGroupEntries(value) {
      return this.carObj.sorted_specs[value].slice(1)
    },

    getCar() {
      this.isLoading = true

      const { endpointGetCar } = this.configuration
      const { slug } = this.$route.params
      const url = `${endpointGetCar}/${this.getTypeToken()}/${slug}/`

      axios
        .get(url)
        .then((response) => {
          const { hasError, errorMessage } = response.data

          if (hasError) {
            this.handleError([errorMessage])
            return false
          }

          const { car } = response.data
          this.carObj = car

          this.handleSuccess()
          this.setRentalDefaults()
          this.getRental()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    setRentalDefaults() {
      const { numOfPayments, annualKm } = this.configuration

      const { numOfPayments: argsNumOfPayments, annualKm: argsAnnualKm } =
        this.getCarArgs('rental')
      this.setField({
        field: 'numOfPayments',
        value: argsNumOfPayments || numOfPayments.default,
      })
      this.setField({
        field: 'annualKm',
        value: argsAnnualKm || annualKm.default,
      })

      this.setPreselectedExtras()
      this.setDepositDefaults()
      this.setBuyBackDefaults()
    },

    setDepositDefaults() {
      const { deposit } = this.configuration
      const { defaultPercentage, factor } = deposit

      // prettier-ignore
      const { deposit: argsDeposit } = this.getCarArgs('rental')
      const value =
        argsDeposit ||
        lib.roundNumberBy100(
          (this.carObj.price_num / factor) * defaultPercentage
        )
      this.setField({ field: 'deposit', value })
    },

    setBuyBackDefaults() {
      const { buybackAmount } = this.configuration
      const { defaultPercentage, factor } = buybackAmount

      // prettier-ignore
      const { buybackAmount: argsBuybackAmount, buyback: argsBuyback } = this.getCarArgs('rental')

      const buybackAmountValue =
        argsBuybackAmount ||
        lib.roundNumberBy100(
          (this.carObj.price_num / factor) * defaultPercentage
        )
      const buybackValue = argsBuyback || this.buyback

      this.setField({ field: 'buybackAmount', value: buybackAmountValue })
      this.setField({ field: 'buyback', value: buybackValue })
    },

    getRental() {
      this.isLoading = true

      const { id } = this.$route.params
      const { hasRentalArgs } = this.$route.query

      /* eslint-disable camelcase */
      const postData = {
        car: id,
        deposit: this.deposit,
        extras: this.extras,
        extrasOptionIds: this.extrasOptionIds,
        numOfPayments: this.numOfPayments,
        annualKm: this.annualKm,
        buyback: this.buybackAmount,
        buyback_active: this.buyback,
      }

      axios
        .post(this.configuration.endpointGetCarRental, postData)
        .then((response) => {
          const { hasError, errorMessage } = response.data

          if (hasError) {
            this.handleError([errorMessage])
            return false
          }

          const { rentalPrice, vat, FinalPrice, extra_price } = response.data

          this.setField({ field: 'rentalPrice', value: rentalPrice })
          this.setField({ field: 'vat', value: vat })
          this.setField({ field: 'finalPrice', value: FinalPrice })
          this.setField({ field: 'extraPrice', value: extra_price })

          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    getCarOptions() {
      this.isLoading = true

      const { id } = this.$route.params
      const typeToken = this.getTypeToken()
      const url = `${this.configuration.endpointGetCarOptions}/${typeToken}/${id}`

      axios
        // .post('http://localhost:8888/leaseitnow-get-car-options')
        .get(url)
        .then((response) => {
          const { hasError, errorMessage } = response.data

          if (hasError) {
            this.handleError([errorMessage])
            return false
          }

          const { carOptionsSorted } = response.data
          const { options, color, packages } = carOptionsSorted

          if (options) {
            this.equipmentOptions = Object.values(options).filter(
              (x) => x.option_type === 'options'
            )
          }

          if (color) {
            this.colorsOptions = Object.values(color).filter(
              (x) => x.option_type === 'color'
            )
          }

          if (packages) {
            this.packagesOptions = Object.values(packages).filter(
              (x) => x.option_type === 'packages'
            )
          }

          this.allOptions = this.equipmentOptions
            .concat(this.colorsOptions)
            .concat(this.packagesOptions)

          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    setPreselectedValues() {
      const { id } = this.$route.params
      this.setField({ field: 'car', value: id })
    },

    onChangeOption() {
      this.extrasIncludedChilds = this.extrasRequiredChilds = []

      if (this.lastChecked) {
        if (this.checkForExtrasExcluded()) {
          return false
        }

        this.checkForOtherExtrasIncluded()
        this.checkForOthersExtrasRequired()
        if (
          this.extrasIncludedChilds.length ||
          this.extrasRequiredChilds.length
        ) {
          this.key = uuidv4()

          this.$nextTick(() => {
            this.showModalExtrasRequired()
          })
        } else {
          this.setExtras()
        }
      } else {
        // console.log('unchecked', this.lastChecked)

        this.checkForSelfExtrasNeeded()

        if (this.extrasNeededChilds.length) {
          this.modalExtrasNeeded.show()
        } else {
          this.setExtras()
        }
      }
    },

    checkForExtrasExcluded() {
      let isInvalid
      const otherOptions = this.allOptions.filter(
        (x) =>
          this.selected.includes(x.optionId) && x.optionId !== this.lastSelected
      )

      const lastOption = this.allOptions.find(
        (x) => x.optionId === this.lastSelected
      )

      otherOptions.forEach((element) => {
        if (element.excludes.includes(this.lastSelected)) {
          isInvalid = true

          this.extrasExcludedChilds.push(element.name)
        }
      })

      if (lastOption) {
        lastOption.excludes.forEach((element) => {
          if (this.selected.includes(element)) {
            isInvalid = true
            const entry = this.allOptions.find(
              (x) => x.optionId === element
            ).name

            this.extrasExcludedChilds.push(entry)
          }
        })
      }

      if (isInvalid) {
        /* eslint-disable no-undef */
        this.extrasExcludedChilds = _.uniqBy(this.extrasExcludedChilds)
        this.modalExtrasExcluded.show()
        this.selected.pop()
      }
      return isInvalid
    },

    checkForOtherExtrasIncluded() {
      if (!this.lastSelected) {
        return false
      }
      const lastOption = this.allOptions.find(
        (x) => x.optionId === this.lastSelected
      )
      this.extrasIncludedChilds = lastOption.includes
    },

    checkForOthersExtrasRequired() {
      if (!this.lastSelected) {
        return false
      }

      const lastOption = this.allOptions.find(
        (x) => x.optionId === this.lastSelected
      )

      this.extrasRequiredChilds = lastOption.requires
    },

    checkForSelfExtrasNeeded() {
      const res = []

      const selectedOptions = this.allOptions.filter((i) =>
        this.selected.includes(i.optionId)
      )

      selectedOptions.forEach((element) => {
        const value = element.optionId
        const requires = element.requires

        requires.forEach((x) => {
          const length = x.length

          if (length === 1) {
            if (x[0] === this.lastSelected) {
              res.push(value)
            }
          } else if (x.some((i) => i === this.lastSelected)) {
            const atLeastOneSelected = x.some((i) => this.selected.includes(i))
            if (!atLeastOneSelected) {
              res.push(value)
            }
          }
        })
      })

      this.extrasNeededChilds = res
    },

    setCarArgs(argsType, data) {
      const sessionStorageKey = `car-${argsType}`
      sessionStorage.setItem(sessionStorageKey, JSON.stringify(data))
    },

    getCarArgs(argsType) {
      const sessionStorageKey = `car-${argsType}`
      const stringifiedData = sessionStorage.getItem(sessionStorageKey)
      return stringifiedData ? JSON.parse(stringifiedData) : {}
    },

    removeCarArgs(argsType) {
      const sessionStorageKey = `car-${argsType}`
      sessionStorage.removeItem(sessionStorageKey)
    },

    setPreselectedExtras() {
      const { selected: argsSelected, extras } = this.getCarArgs('extras')
      this.selected = argsSelected || this.selected

      this.setField({ field: 'extrasOptionIds', value: this.selected })
      /* eslint-disable no-undef */

      this.setField({ field: 'extras', value: extras || 0 })
    },

    setExtras() {
      const { id } = this.$route.params

      const prices = this.allOptions
        .filter((x) => this.selected.includes(x.optionId))
        .map((x) => x.price)

      this.setField({ field: 'extrasOptionIds', value: this.selected })
      /* eslint-disable no-undef */
      this.setField({ field: 'extras', value: _.sum(prices) })

      this.setCarArgs('extras', {
        selected: this.selected,
        extras: _.sum(prices),
      })
      this.getRental()
    },

    onChangeConfig() {
      // annualKm, deposit, numOfPayments, buybackAmount
      this.setCarArgs('rental', {
        annualKm: this.annualKm,
        deposit: this.deposit,
        numOfPayments: this.numOfPayments,
        buybackAmount: this.buybackAmount,
        buyback: this.buyback,
      })
      this.getRental()
    },

    onChangeVat(value) {
      this.vatIncluded = value
    },

    onClickRequestOffer() {
      const { language, slug, id } = this.$route.params
      this.$router.push({ name: 'apply', params: { language, slug, id } })

      const carConfig = {
        deposit: this.deposit,
        extrasOptionIds: this.extrasOptionIds,
        numOfPayments: this.numOfPayments,
        annualKm: this.annualKm,
        buyback: this.buybackAmount,
        extras: this.extras,
        buybackActive: this.buyback,
        vatIncluded: this.vatIncluded,
      }
      const { sessionStorageConfig } = constants.leaseitnow
      sessionStorage.setItem(sessionStorageConfig, JSON.stringify(carConfig))

      lib.scrollToTop()
    },

    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
    },

    handleError(messages) {
      this.errorMsgs = messages
      this.isLoading = false
    },

    onUndoExtras(value) {
      /* eslint-disable no-undef */
      this.selected = _.remove(this.selected, (x) => x !== value)
    },

    onSubmitExtras(values) {
      values.forEach((element) => {
        if (!this.selected.includes(element)) {
          this.selected.push(element)
        }
      })
      this.setExtras()
    },

    onUndoExtrasNeeded(value) {
      this.selected.push(value)
    },

    onSubmitExtrasNeeded(values) {
      /* eslint-disable no-undef */
      this.selected = _.remove(this.selected, (x) => !values.includes(x))
      this.setExtras()
    },
  },
}
</script>
